import React from 'react';
import { ArrowRight, Mail, Phone, MapPin, Globe, Users } from 'lucide-react';

const FooterCTA = ({ onDemoClick, isDark }) => {
  return (
    <footer className={isDark ? 'bg-slate-950' : 'bg-slate-900'} data-testid="footer-section">
      {/* CTA Section */}
      <div className="section-padding border-b border-slate-800">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            See Skydoc in Action Across a Full Patient Journey
          </h2>
          <p className="text-lg text-slate-400 mb-8 max-w-2xl mx-auto">
            Not just documentation. Real workflow automation across intake, care delivery, 
            follow-up, and revenue.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={onDemoClick}
              className="btn-primary inline-flex items-center gap-2"
              data-testid="footer-book-demo-btn"
            >
              Book a Demo
              <ArrowRight className="w-5 h-5" />
            </button>
            <button 
              className="btn-secondary bg-slate-800 border-slate-700 text-white hover:bg-slate-700"
              data-testid="footer-talk-sales-btn"
            >
              Talk to Sales
            </button>
          </div>
        </div>
      </div>

      {/* EMR Integration & Pricing Contact Section */}
      <div className="border-b border-slate-800 py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-white mb-2">
              EMR Integration & Customized Pricing
            </h3>
            <p className="text-slate-400">
              Contact our regional teams for enterprise solutions and integration support
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* India Contact */}
            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-orange-400" />
                </div>
                <div>
                  <h4 className="text-white font-semibold">Skydoc India</h4>
                  <p className="text-sm text-slate-400">Enterprise & Integration Support</p>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-3 bg-slate-800 rounded-lg">
                  <Users className="w-4 h-4 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-white font-medium">Anusha Bhupati</p>
                    <a href="tel:+918790919141" className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1">
                      <Phone className="w-3 h-3" /> +91 87909 19141
                    </a>
                    <a href="mailto:anusha.bhupati@exoralabs.com" className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1">
                      <Mail className="w-3 h-3" /> anusha.bhupati@exoralabs.com
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 bg-slate-800 rounded-lg">
                  <Users className="w-4 h-4 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-white font-medium">Nandini K</p>
                    <a href="tel:+919731306219" className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1">
                      <Phone className="w-3 h-3" /> +91 97313 06219
                    </a>
                    <a href="mailto:nandini.k@exoralabs.com" className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1">
                      <Mail className="w-3 h-3" /> nandini.k@exoralabs.com
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* UK & Worldwide Contact */}
            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <Globe className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h4 className="text-white font-semibold">Skydoc UK & Worldwide</h4>
                  <p className="text-sm text-slate-400">Global Enterprise Solutions</p>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-3 bg-slate-800 rounded-lg">
                  <Users className="w-4 h-4 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-white font-medium">Dr. Rohith G</p>
                    <a href="tel:+447921398886" className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1">
                      <Phone className="w-3 h-3" /> +44 7921 398886
                    </a>
                    <a href="mailto:drrohithg@gmail.com" className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1">
                      <Mail className="w-3 h-3" /> drrohithg@gmail.com
                    </a>
                  </div>
                </div>

                <div className="p-3 bg-blue-900/30 rounded-lg border border-blue-800/50">
                  <p className="text-sm text-blue-300">
                    For NHS integration, EMIS/SystmOne connectivity, and UK healthcare compliance requirements.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Links */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        <div className="grid md:grid-cols-4 gap-8 mb-12">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <img 
                src="/skydoc-icon-transparent.png" 
                alt="SkyDoc" 
                className="h-9 w-auto"
              />
              <span className="text-xl font-bold text-white">SkyDoc</span>
            </div>
            <p className="text-slate-400 text-sm mb-4">
              The Operating System for Clinical Workflows. Automating the entire clinical 
              episode from intake to follow-up.
            </p>
            <p className="text-slate-500 text-xs">
              Powered by Exora Labs
            </p>
          </div>

          {/* Product */}
          <div>
            <h4 className="text-white font-semibold mb-4">Product</h4>
            <ul className="space-y-2">
              {['Voice Agent', 'Clinical Copilot', 'Documentation', 'Care Execution', 'Follow-up', 'Analytics'].map((item) => (
                <li key={item}>
                  <a href="#product" className="text-slate-400 hover:text-white text-sm transition-colors">
                    {item}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-white font-semibold mb-4">Company</h4>
            <ul className="space-y-2">
              {['About', 'Careers', 'Blog', 'Press', 'Contact'].map((item) => (
                <li key={item}>
                  <a href="#" className="text-slate-400 hover:text-white text-sm transition-colors">
                    {item}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Contact */}
          <div>
            <h4 className="text-white font-semibold mb-4">Quick Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-2 text-slate-400 text-sm">
                <Mail className="w-4 h-4" />
                hello@skydoc.ai
              </li>
              <li className="text-slate-400 text-sm">
                <span className="text-slate-500 block text-xs mb-1">India</span>
                +91 87909 19141
              </li>
              <li className="text-slate-400 text-sm">
                <span className="text-slate-500 block text-xs mb-1">UK & Worldwide</span>
                +44 7921 398886
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="pt-8 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm">
            © 2025 Skydoc AI by Exora Labs. All rights reserved.
          </p>
          <div className="flex gap-6">
            <a href="#" className="text-slate-400 hover:text-white text-sm transition-colors">
              Privacy Policy
            </a>
            <a href="#" className="text-slate-400 hover:text-white text-sm transition-colors">
              Terms of Service
            </a>
            <a href="#" className="text-slate-400 hover:text-white text-sm transition-colors">
              Security
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default FooterCTA;
